/**
 * R-IMPORT 2 catalogs store — interface com 4 catálogos novos do R-IMPORT 1.
 *
 * Entidades cobertas (todas com endpoints CRUD em /api/v1/*):
 *   - service_categories     (/api/v1/service-categories)
 *   - relationship_roles     (/api/v1/relationship-roles)
 *   - client_segments        (/api/v1/client-segments)
 *   - contract_templates     (/api/v1/contract-templates)
 *
 * Padrão: useQuery pra list + mutations via api.post/put/delete + invalidação
 * automática (mesma estratégia de `clients-store`).
 *
 * NÃO duplica funcionalidade do legado `catalogs-store.ts` (sistema R0
 * dept+list+label genérico). Os 4 catálogos novos têm schemas próprios
 * (cor, ícone, flags semânticos, etc) que não cabem no genérico.
 */

import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { api, hasToken } from './api';

// ─── service_categories ────────────────────────────────────────────────

export interface ServiceCategory {
  id: string;
  empresaId: string;
  slug: string;
  nome: string;
  descricao: string | null;
  icone: string | null;
  cor: string | null;
  ordem: number;
  ativo: boolean;
  criadoEmIso: string;
}

export interface CreateServiceCategoryInput {
  slug: string;
  nome: string;
  descricao?: string | null;
  icone?: string | null;
  cor?: string | null;
  ordem?: number;
}

export interface UpdateServiceCategoryInput {
  slug?: string;
  nome?: string;
  descricao?: string | null;
  icone?: string | null;
  cor?: string | null;
  ordem?: number;
  ativo?: boolean;
}

export function useServiceCategories(opts: { includeInactive?: boolean } = {}) {
  const params = new URLSearchParams();
  if (opts.includeInactive) params.set('includeInactive', 'true');
  const qs = params.toString();
  return useQuery({
    queryKey: ['v1', 'service-categories', opts.includeInactive ?? false] as const,
    queryFn: () =>
      api.get<ServiceCategory[]>(`/api/v1/service-categories${qs ? `?${qs}` : ''}`),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

export function useServiceCategoryActions() {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['v1', 'service-categories'] });

  const create = useMutation({
    mutationFn: (input: CreateServiceCategoryInput) =>
      api.post<ServiceCategory>('/api/v1/service-categories', input),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateServiceCategoryInput }) =>
      api.put<ServiceCategory>(`/api/v1/service-categories/${id}`, input),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/v1/service-categories/${id}`),
    onSuccess: invalidate,
  });

  return { create, update, remove };
}

// ─── relationship_roles ────────────────────────────────────────────────

export interface RelationshipRole {
  id: string;
  empresaId: string;
  slug: string;
  nome: string;
  descricao: string | null;
  ordem: number;
  ativo: boolean;
  recebeNotificacoesPadrao: boolean;
  podeAutorizarAtendimento: boolean;
  criadoEmIso: string;
}

export interface CreateRelationshipRoleInput {
  slug: string;
  nome: string;
  descricao?: string | null;
  ordem?: number;
  recebeNotificacoesPadrao?: boolean;
  podeAutorizarAtendimento?: boolean;
}

export interface UpdateRelationshipRoleInput {
  slug?: string;
  nome?: string;
  descricao?: string | null;
  ordem?: number;
  ativo?: boolean;
  recebeNotificacoesPadrao?: boolean;
  podeAutorizarAtendimento?: boolean;
}

export function useRelationshipRoles(opts: { includeInactive?: boolean } = {}) {
  const params = new URLSearchParams();
  if (opts.includeInactive) params.set('includeInactive', 'true');
  const qs = params.toString();
  return useQuery({
    queryKey: ['v1', 'relationship-roles', opts.includeInactive ?? false] as const,
    queryFn: () =>
      api.get<RelationshipRole[]>(`/api/v1/relationship-roles${qs ? `?${qs}` : ''}`),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

export function useRelationshipRoleActions() {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['v1', 'relationship-roles'] });

  const create = useMutation({
    mutationFn: (input: CreateRelationshipRoleInput) =>
      api.post<RelationshipRole>('/api/v1/relationship-roles', input),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRelationshipRoleInput }) =>
      api.put<RelationshipRole>(`/api/v1/relationship-roles/${id}`, input),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/v1/relationship-roles/${id}`),
    onSuccess: invalidate,
  });

  return { create, update, remove };
}

// ─── contract_templates ─────────────────────────────────────────────────

export type ContractTemplateTipo = 'recorrente' | 'projeto';

export interface ContractTemplate {
  id: string;
  empresaId: string;
  nome: string;
  descricao: string | null;
  tipo: ContractTemplateTipo;
  periodicidadePreventiva: string | null;
  slaHorasPadrao: number | null;
  qtdChamadosCorretivosMes: number | null;
  temPlantao: boolean;
  qtdPlantoesMes: number | null;
  valorCorretivoExtra: number | null;
  valorPlantaoExtra: number | null;
  servicosDefaultIds: string[];
  ativo: boolean;
  ordem: number;
  criadoEmIso: string;
}

export interface CreateContractTemplateInput {
  nome: string;
  tipo: ContractTemplateTipo;
  descricao?: string | null;
  periodicidadePreventiva?: string | null;
  slaHorasPadrao?: number | null;
  qtdChamadosCorretivosMes?: number | null;
  temPlantao?: boolean;
  qtdPlantoesMes?: number | null;
  valorCorretivoExtra?: number | null;
  valorPlantaoExtra?: number | null;
  servicosDefaultIds?: string[];
  ordem?: number;
}

export interface UpdateContractTemplateInput
  extends Partial<CreateContractTemplateInput> {
  ativo?: boolean;
}

export function useContractTemplates(opts: {
  includeInactive?: boolean;
  tipo?: ContractTemplateTipo;
} = {}) {
  const params = new URLSearchParams();
  if (opts.includeInactive) params.set('includeInactive', 'true');
  if (opts.tipo) params.set('tipo', opts.tipo);
  const qs = params.toString();
  return useQuery({
    queryKey: [
      'v1',
      'contract-templates',
      opts.includeInactive ?? false,
      opts.tipo ?? 'all',
    ] as const,
    queryFn: () =>
      api.get<ContractTemplate[]>(`/api/v1/contract-templates${qs ? `?${qs}` : ''}`),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

export function useContractTemplateActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['v1', 'contract-templates'] });

  const create = useMutation({
    mutationFn: (input: CreateContractTemplateInput) =>
      api.post<ContractTemplate>('/api/v1/contract-templates', input),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateContractTemplateInput }) =>
      api.put<ContractTemplate>(`/api/v1/contract-templates/${id}`, input),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/v1/contract-templates/${id}`),
    onSuccess: invalidate,
  });

  return { create, update, remove };
}
